/**
 * An entity's history from the case change log (ADR-0090): the events naming its `_uid`,
 * newest first; with `asOf` (*Spis k dátumu*, R-D4) only those the case had learned by then. A
 * node without a `_uid` (a document, a passage, an entity written before the log) has no
 * history, and nothing is read for it.
 */
import { useQuery } from '@tanstack/react-query'
import { type CaseEventDto, caseEventsApi } from '@/services/api/case-events'
import { ApiError } from '@/services/api/client'

export const entityHistoryKeys = {
  all: (caseId: string) => ['case', caseId, 'events'] as const,
  entity: (caseId: string, uid: string | null, asOf?: string) =>
    [...entityHistoryKeys.all(caseId), 'entity', uid, asOf ?? 'now'] as const,
}

/** A refusal (not a member, unknown case) answers the same again; an outage may not. */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  const refused = error instanceof ApiError && error.status >= 400 && error.status < 500
  return !refused && failureCount < 2
}

export function useEntityHistory(caseId: string, uid: string | null, asOf?: string) {
  return useQuery<CaseEventDto[]>({
    queryKey: entityHistoryKeys.entity(caseId, uid, asOf),
    queryFn: async ({ signal }) => {
      const response = await caseEventsApi.listForEntity(caseId, uid as string, { asOf, signal })
      return response.events
    },
    enabled: Boolean(caseId && uid),
    retry: retryUnlessRefused,
  })
}
